import { sample } from 'effector'
import { isType } from 'variant'
import { appearanceFeature } from '~/features/appearance'
import { Dirty, Empty } from './index.h'
import {
  $loadQueue,
  $structure,
  loadContent,
  loadModule,
  setStructure,
} from './model'

const $identifier = appearanceFeature.$currentIdentifier
const getQueueKey = (identifier: string, id: string) => `${identifier}:${id}`

// load dirty components on the page load
sample({
  clock: loadModule,
  source: {
    structure: $structure,
    queue: $loadQueue,
  },
  filter: ({ structure }) => (structure ? structure?.size > 0 : false),
  fn: ({ structure, queue }, { identifier }) => {
    // Create a new array to avoid mutating the original queue
    const updatedQueue = [...queue]
    const queuedKeys = new Set(
      queue.map(({ identifier, component }) =>
        getQueueKey(identifier, component.id)
      )
    )

    for (const [, { data, desc }] of structure!)
      if (
        isType(data, Dirty) &&
        !queuedKeys.has(getQueueKey(identifier, desc.id))
      ) {
        queuedKeys.add(getQueueKey(identifier, desc.id))
        updatedQueue.push({ identifier, component: desc })
      }

    return updatedQueue
  },
  target: $loadQueue,
})

sample({
  clock: setStructure,
  source: $loadQueue,
  filter: (_, { structure }) => (structure ? structure.size > 0 : false),
  fn: (queue, { identifier, structure }) => {
    const queuedKeys = new Set(
      queue.map(({ identifier, component }) =>
        getQueueKey(identifier, component.id)
      )
    )
    const updatedQueue = [...queue]

    for (const [, { data, desc }] of structure!) {
      const queueKey = getQueueKey(identifier, desc.id)

      if (isType(data, Dirty) && !queuedKeys.has(queueKey)) {
        queuedKeys.add(queueKey)
        updatedQueue.push({ identifier, component: desc })
      }
    }

    return updatedQueue
  },
  target: $loadQueue,
})

// add row or banners to the load queue if it is empty or dirty
// do not reload already loaded and not refreshable components
sample({
  clock: loadContent,
  source: { structure: $structure, queue: $loadQueue, identifier: $identifier },
  filter: ({ structure, identifier }, { id }) => {
    if (!identifier) return false

    const data = structure?.get(id)?.data
    return data == null || isType(data, Empty) || isType(data, Dirty)
  },
  fn: ({ queue, identifier }, comp) => {
    if (!identifier) return queue

    const queueKey = getQueueKey(identifier, comp.id)
    const queued = queue.some(
      ({ identifier, component }) =>
        getQueueKey(identifier, component.id) === queueKey
    )

    return queued ? queue : [...queue, { identifier, component: comp }]
  },
  target: $loadQueue,
})
